/** service_type enum('airport'|'golf'|...)을 Badge용 카테고리('transfer'|'tour'|'shopping')로 매핑 */
export function getBadgeServiceType(serviceType) {
  if (serviceType === 'tour') return 'tour';
  if (serviceType === 'shopping') return 'shopping';
  return 'transfer';
}

/** Shared service type labels — single source of truth.
 *  다국어 대응: 런타임에 t()를 주입받는 factory 함수 제공. 기존 static 객체(한국어)는
 *  호환 목적으로 유지하되, 서비스 페이지 신규 코드는 make* factory를 사용할 것. */

export const serviceLabels = {
  airport: '프라이빗 이동 - 공항',
  golf: '프라이빗 이동 - 골프',
  ski: '프라이빗 이동 - 리조트',
  hospital: '프라이빗 이동 - 병원',
  pet: '프라이빗 이동 - 반려동물',
  business: '프라이빗 이동 - 비즈니스',
};

/** i18n t()를 주입받아 현재 언어 serviceLabels 반환 */
export function makeServiceLabels(t) {
  return {
    airport: t('serviceLabel.airport'),
    golf: t('serviceLabel.golf'),
    ski: t('serviceLabel.ski'),
    hospital: t('serviceLabel.hospital'),
    pet: t('serviceLabel.pet'),
    business: t('serviceLabel.business'),
  };
}

// 이동 정보 방향 라벨 (서비스별 + direction enum)
export const directionLabels = {
  // API direction enum — 프론트 전송값 (BookingSettings → create)
  'from-airport':  '공항 → 목적지',
  'to-airport':    '출발지 → 공항',
  'to-hospital':   '출발지 → 병원',
  'from-hospital': '병원 → 도착지',
  'round-trip':    '왕복',
  // API direction enum — 백엔드 저장값 (기존 호환)
  pickup:  '공항 → 목적지',
  sending: '출발지 → 공항',
  // 서비스 타입 기본값 (direction null 시 fallback)
  airport:  '출발지 → 공항',
  golf:     '출발지 → 골프장 → 도착지',
  ski:      '출발지 → 리조트 → 도착지',
  hospital: '출발지 → 병원',
  pet:      '출발지 → 도착지',
  business: '이용 시작지 → 이용 종료지',
  tour:     '투어 일정',
};

/** i18n t()를 주입받아 현재 언어 directionLabels 반환 */
export function makeDirectionLabels(t) {
  const fromAirport = t('directionLabel.from_airport');
  const toAirport = t('directionLabel.to_airport');
  return {
    'from-airport': fromAirport,
    'to-airport': toAirport,
    'to-hospital': t('directionLabel.to_hospital'),
    'from-hospital': t('directionLabel.from_hospital'),
    'round-trip': t('directionLabel.round_trip'),
    pickup: fromAirport,
    sending: toAirport,
    airport: t('directionLabel.airport'),
    golf: t('directionLabel.golf'),
    ski: t('directionLabel.ski'),
    hospital: t('directionLabel.hospital'),
    pet: t('directionLabel.pet'),
    business: t('directionLabel.business'),
    tour: t('tour.movement_route'),
  };
}

// 서비스 유형 (short form — service.airport 등 이미 i18n namespace 존재, t('service.airport')로 직접 사용 권장)
export const serviceTypeLabels = {
  airport:  '공항',
  golf:     '골프',
  ski:      '리조트',
  hospital: '병원',
  pet:      '반려동물',
  business: '비즈니스',
};

export function makeServiceTypeLabels(t) {
  return {
    airport: t('service.airport'),
    golf: t('service.golf'),
    ski: t('service.ski'),
    hospital: t('service.hospital'),
    pet: t('service.pet'),
    business: t('service.business'),
  };
}

// 도착지 표시
export const destinationLabels = {
  airport:  '공항',
  golf:     '골프장',
  ski:      '리조트',
  hospital: '병원',
  pet:      '도착지',
  business: '이용 종료지',
};

export function makeDestinationLabels(t) {
  return {
    airport: t('destinationLabel.airport'),
    golf: t('destinationLabel.golf'),
    ski: t('destinationLabel.ski'),
    hospital: t('destinationLabel.hospital'),
    pet: t('destinationLabel.pet'),
    business: t('destinationLabel.business'),
  };
}

// 파트너 유형
export const partnerTypeLabels = {
  interpreter: '통역사',
  callvan:     '콜밴 기사',
  driver:      '드라이버',
  other:       '기타',
};

// 알림 채널
export const notificationChannelLabels = {
  alimtalk: '알림톡',
  email:    '이메일',
};

// 알림 유형
export const notificationTypeLabels = {
  reservation: '예약',
  payment:     '결제',
};

// FAQ 카테고리
export const faqCategoryLabels = {
  general:     '일반',
  reservation: '예약',
  service:     '서비스',
};

// 언어
export const languageLabels = {
  ko: '한국어',
  en: 'English',
  zh: '中文',
  ja: '日本語',
};

// 유저 역할
export const roleLabels = {
  user:  '일반 회원',
  admin: '관리자',
};

// 옵션 코드/이름 → 한글 매핑
export const optionNameLabels = {
  passengers: '이용 인원',
  luggage: '수하물 추가',
  child_seat: '유아용 카시트',
  pet_carrier: '반려동물 캐리어',
  wheelchair: '휠체어',
  extra_luggage: '추가 수하물',
};

export function makeOptionNameLabels(t) {
  return {
    passengers: t('optionName.passengers'),
    luggage: t('optionName.luggage'),
    child_seat: t('optionName.child_seat'),
    pet_carrier: t('optionName.pet_carrier'),
    wheelchair: t('optionName.wheelchair'),
    extra_luggage: t('optionName.extra_luggage'),
    // 백엔드가 한국어 이름으로 내려주는 경우도 매핑 (fallback)
    '이용 인원': t('optionName.passengers'),
    '수하물 추가': t('optionName.luggage'),
    '유아용 카시트': t('optionName.child_seat'),
    '반려동물 캐리어': t('optionName.pet_carrier'),
    '휠체어': t('optionName.wheelchair'),
    '추가 수하물': t('optionName.extra_luggage'),
  };
}

// 백엔드 기본 옵션 코드 (서비스별 옵션과 중복되므로 필터링 대상)
const duplicateBaseCodes = new Set(['passengers', 'luggage']);
// 기획에 없는 옵션 (백엔드에서 내려와도 프론트에서 제외)
const excludedCodes = new Set(['pocket_wifi', 'wifi']);

/**
 * 백엔드 selected_options 정제
 * - 기본 코드(passengers/luggage)와 서비스별 코드(airport_passengers 등) 중복 제거
 * - 기획에 없는 옵션(pocket_wifi) 제외
 */
export function filterSelectedOptions(options) {
  if (!options || !Array.isArray(options)) return [];
  // 서비스별 코드(airport_passengers 등)가 있으면 기본 코드(passengers) 제거
  const hasServiceCode = options.some((o) => o.code && o.code.includes('_') && !excludedCodes.has(o.code));
  return options.filter((o) => {
    if (excludedCodes.has(o.code) || excludedCodes.has(o.name)) return false;
    if (hasServiceCode && duplicateBaseCodes.has(o.code)) return false;
    return true;
  });
}

// 소셜 로그인
export const socialProviderLabels = {
  kakao:  '카카오',
  naver:  '네이버',
  google: '구글',
};
